/**
 * Utilidades para formateo de moneda
 */

export interface CurrencyFormatOptions {
  /** Símbolo de la moneda (por defecto: '$') */
  symbol?: string;
  /** Posición del símbolo (por defecto: 'before') */
  symbolPosition?: 'before' | 'after';
  /** Separador de miles (por defecto: '.') */
  thousandsSeparator?: string;
  /** Separador decimal (por defecto: ',') */
  decimalSeparator?: string;
  /** Número de decimales (por defecto: 2) */
  decimals?: number;
  /** Mostrar ceros decimales (por defecto: true) */
  showDecimals?: boolean;
}

/**
 * Formatea un número como moneda
 * @param amount - Cantidad a formatear
 * @param options - Opciones de formateo
 * @returns String formateado como moneda
 *
 * @example
 * formatCurrency(800000) // "$800.000,00"
 * formatCurrency(800000, { symbol: '€', symbolPosition: 'after' }) // "800.000,00€"
 * formatCurrency(800000, { decimals: 0 }) // "$800.000"
 * formatCurrency(800000, { thousandsSeparator: ',', decimalSeparator: '.' }) // "$800,000.00"
 */
export function formatCurrency(
  amount: number | string,
  options: CurrencyFormatOptions = {}
): string {
  const {
    symbol = '$',
    symbolPosition = 'before',
    thousandsSeparator = '.',
    decimalSeparator = ',',
    decimals = 2,
    showDecimals = true,
  } = options;

  // Convertir a número
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Validar que sea un número válido
  if (isNaN(numAmount)) {
    return `${symbol}0${showDecimals ? decimalSeparator + '0'.repeat(decimals) : ''}`;
  }

  // Formatear el número usando es-ES para obtener punto como separador de miles
  const formattedNumber = numAmount.toLocaleString('es-ES', {
    minimumFractionDigits: showDecimals ? decimals : 0,
    maximumFractionDigits: decimals,
    useGrouping: true,
  });

  // Reemplazar separadores por los personalizados
  // es-ES ya usa punto para miles y coma para decimales, que es lo que queremos
  const parts = formattedNumber.split(',');
  const integerPart = parts[0].replace(/\./g, thousandsSeparator);
  const decimalPart = parts[1] || '';

  const result =
    showDecimals && decimals > 0
      ? `${integerPart}${decimalSeparator}${decimalPart.padEnd(decimals, '0')}`
      : integerPart;

  // Aplicar símbolo
  return symbolPosition === 'before'
    ? `${symbol}${result}`
    : `${result}${symbol}`;
}

/**
 * Formatea un número como moneda con configuración por defecto del proyecto
 * (formato: 800.000,00)
 * @param amount - Cantidad a formatear
 * @returns String formateado como moneda
 *
 * @example
 * formatCurrencyDefault(800000) // "$800.000,00"
 * formatCurrencyDefault(1500.5) // "$1.500,50"
 */
export function formatCurrencyDefault(amount: number | string): string {
  return formatCurrency(amount, {
    symbol: '$',
    symbolPosition: 'before',
    thousandsSeparator: '.',
    decimalSeparator: ',',
    decimals: 2,
    showDecimals: true,
  });
}

/**
 * Formatea un número como moneda sin decimales
 * @param amount - Cantidad a formatear
 * @returns String formateado como moneda sin decimales
 *
 * @example
 * formatCurrencyNoDecimals(800000) // "$800.000"
 * formatCurrencyNoDecimals(1500.5) // "$1.501"
 */
export function formatCurrencyNoDecimals(amount: number | string): string {
  return formatCurrency(amount, {
    symbol: '$',
    symbolPosition: 'before',
    thousandsSeparator: '.',
    decimalSeparator: ',',
    decimals: 0,
    showDecimals: false,
  });
}

/**
 * Formatea un número como moneda con símbolo después
 * @param amount - Cantidad a formatear
 * @param currencySymbol - Símbolo de la moneda (por defecto: '$')
 * @returns String formateado como moneda con símbolo después
 *
 * @example
 * formatCurrencyAfter(800000) // "800.000,00$"
 * formatCurrencyAfter(800000, '€') // "800.000,00€"
 */
export function formatCurrencyAfter(
  amount: number | string,
  currencySymbol: string = '$'
): string {
  return formatCurrency(amount, {
    symbol: currencySymbol,
    symbolPosition: 'after',
    thousandsSeparator: '.',
    decimalSeparator: ',',
    decimals: 2,
    showDecimals: true,
  });
}

/**
 * Parsea una cadena de moneda formateada de vuelta a número
 * @param currencyString - String de moneda formateada
 * @returns Número parseado
 *
 * @example
 * parseCurrency("$800.000,00") // 800000
 * parseCurrency("800.000,00€") // 800000
 */
export function parseCurrency(currencyString: string): number {
  if (!currencyString || typeof currencyString !== 'string') {
    return 0;
  }

  // Remover símbolos de moneda comunes
  const cleaned = currencyString
    .replace(/[$€£¥₹]/g, '')
    .replace(/\./g, '') // Remover separadores de miles
    .replace(',', '.'); // Convertir separador decimal a punto

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}
